import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  SimpleGrid,
  Switch,
  Text,
  Box,
  Flex,
  Spinner,
  Center,
  IconButton,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Plus, Trash2 } from "lucide-react";
import {
  GetUserById,
  CreateUser,
  UpdateUser,
  ActivateUser,
  DeactivateUser,
  GetUserAccountStatus,
  GetUserImages,
  UploadImages,
  RemoveImage,
  grantManualPremium,
  revokeManualPremium,
} from "../../services/backend";
import {
  isUserPremium,
  premiumUntilLabel,
  addMonthsFromNow,
} from "../../util/premiumAccess";
import { resolveImageUrl, resolveProfileImageUrl } from "../../util/profileImage";
import { isBuggyImageUri } from "../../util/profileQuality";
const EDUCATIONS = [
  "High School",
  "Some College",
  "Associate",
  "Bachelors degree",
  "Masters degree",
  "Doctorate",
  "Trade School",
  "Other",
];

const DRINKING_OPTIONS = ["Never", "Occasinally", "Socially", "Regularly", "Prefer not to say"];
const SMOKING_OPTIONS = [
  "Non-smoker",
  "Occasionally",
  "Social smoker",
  "Regular smoker",
  "Trying to quit",
  "Prefer not to say",
];
const WORKOUT_OPTIONS = [
  "Not active",
  "occasionally",
  "Active few times week",
  "Very active",
  "Fitness lifestyle",
  "Prefer not to say",
];
const RELIGION_OPTIONS = [
  "Christian  Catholic",
  "Christian  Protestant",
  "Christian  Other",
  "Muslim",
  "Jewish",
  "Hindu",
  "Buddhist",
  "Spiritual but not religious",
  "Agnostic",
  "Atheist",
  "Other",
  "Prefer not to say",
];

function optionsWithCurrent(options, current) {
  const value = typeof current === "string" ? current.trim() : "";
  if (!value || options.includes(value)) return options;
  return [value, ...options];
}
const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  bio: "",
  occupation: "",
  education: "",
  country: "",
  age: "",
  gender: "",
  drinking: "",
  smoking: "",
  workout: "",
  religion: "",
  isProfileComplete: false,
  isVerified: false,
};

function SectionTitle({ children }) {
  const color = useColorModeValue("gray.500", "whiteAlpha.500");
  return (
    <Text
      fontSize="xs"
      fontWeight="semibold"
      letterSpacing="0.08em"
      textTransform="uppercase"
      color={color}
      mt={1}
      mb={1}
    >
      {children}
    </Text>
  );
}

export default function AdminUserEditor({ userId, mode = "edit", isOpen, onClose, onSaved }) {
  const toast = useToast();
  const isCreate = mode === "create";
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accountActive, setAccountActive] = useState(true);
  const [premiumInfo, setPremiumInfo] = useState({
    isPremium: false,
    source: "stripe",
    premiumUntil: null,
  });
  const [grantDuration, setGrantDuration] = useState("1year");
  const [customUntil, setCustomUntil] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [images, setImages] = useState([]);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const clearPendingImageUrls = (list) => {
    (list || []).forEach((img) => {
      if (img?.pending && img?.url?.startsWith?.("blob:")) {
        URL.revokeObjectURL(img.url);
      }
    });
  };

  const panelBg = useColorModeValue("rgba(255,255,255,0.98)", "rgba(18,18,20,0.96)");
  const border = useColorModeValue("gray.200", "whiteAlpha.100");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const titleColor = useColorModeValue("gray.900", "white");
  const sectionTint = useColorModeValue("brand.50", "rgba(52,211,153,0.08)");
  const photoTileBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const inputBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const focus = {
    borderColor: "brand.400",
    boxShadow: "0 0 0 1px var(--chakra-colors-brand-400)",
  };
  const inputProps = {
    size: "sm",
    borderRadius: "lg",
    bg: inputBg,
    borderColor: inputBorder,
    color: titleColor,
    _focus: focus,
  };

  useEffect(() => {
    if (!isOpen) return;

    if (isCreate) {
      setLoading(false);
      setSaving(false);
      setUploadingPhoto(false);
      setForm(emptyForm);
      setImages((prev) => {
        clearPendingImageUrls(prev);
        return [];
      });
      setAvatarUrl(null);
      setAccountActive(true);
      setPremiumInfo({ isPremium: false, source: "stripe", premiumUntil: null });
      setGrantDuration("1year");
      setCustomUntil("");
      return;
    }

    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setImages([]);
      setAvatarUrl(null);
      try {
        const [u, status, imgs] = await Promise.all([
          GetUserById(userId),
          GetUserAccountStatus(userId).catch(() => ({ status: true })),
          GetUserImages(userId).catch(() => []),
        ]);
        if (cancelled) return;
        setAccountActive(status?.status !== false && status?.status !== "inactive");
        setForm({
          firstName: u.firstName ?? "",
          lastName: u.lastName ?? "",
          email: u.email ?? "",
          bio: u.bio ?? "",
          occupation: u.occupation ?? "",
          education: u.education ?? "",
          country: u.country ?? "",
          age: u.age != null ? String(u.age) : "",
          gender: u.gender ?? "",
          drinking: u.drinking ?? "",
          smoking: u.smoking ?? "",
          workout: u.workout ?? "",
          religion: u.religion ?? "",
          isProfileComplete: !!u.isProfileComplete,
          isVerified: !!(u.isVerified ?? u.verified),
        });
        setPremiumInfo({
          isPremium: isUserPremium(u),
          source: u.subscriptionSource ?? "stripe",
          premiumUntil: u.premiumUntil ?? null,
        });

        const list = Array.isArray(imgs) ? imgs : [];
        const resolved = list
          .map((img) => ({
            id: img.id ?? img.dbId ?? img.fileName ?? img.storagePath,
            url: resolveImageUrl(img),
          }))
          .filter((img) => img.url && !isBuggyImageUri(img.url) && !img.url.includes("ui-avatars.com"));
        setImages(resolved);

        const avatar = resolveProfileImageUrl({
          profileImageUri: u.profileImageUri,
          images: list,
          firstName: u.firstName,
          lastName: u.lastName,
        });
        setAvatarUrl(
          avatar && !avatar.includes("ui-avatars.com") && !isBuggyImageUri(avatar) ? avatar : null
        );
      } catch (err) {
        if (!cancelled) {
          toast({
            title: "Could not load user",
            description: err?.response?.data?.message || err.message,
            status: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userId, isCreate, toast]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const mapImageList = (imgs) => {
    const list = Array.isArray(imgs) ? imgs : [];
    return list
      .map((img) => ({
        id: img.id ?? img.dbId ?? img.fileName ?? img.storagePath,
        dbId: img.id ?? img.dbId,
        url: resolveImageUrl(img),
        pending: false,
      }))
      .filter((img) => img.url && !isBuggyImageUri(img.url) && !img.url.includes("ui-avatars.com"));
  };

  const reloadImages = async (targetUserId) => {
    if (!targetUserId) return;
    try {
      const imgs = await GetUserImages(targetUserId);
      const resolved = mapImageList(imgs);
      setImages(resolved);
      if (resolved[0]?.url) setAvatarUrl(resolved[0].url);
    } catch {
      /* keep current */
    }
  };

  const uploadImageFile = async (targetUserId, file) => {
    const data = new FormData();
    data.append("UserId", targetUserId);
    data.append("imageCollection", file);
    await UploadImages(data);
  };

  const handlePickPhotos = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    if (!files.length) return;

    if (isCreate) {
      const next = files.map((file) => ({
        id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        url: URL.createObjectURL(file),
        file,
        pending: true,
      }));
      setImages((prev) => [...prev, ...next]);
      setAvatarUrl((prev) => prev || next[0]?.url || null);
      return;
    }

    if (!userId) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        await uploadImageFile(userId, file);
      }
      await reloadImages(userId);
      toast({
        title: files.length === 1 ? "Photo added" : `${files.length} photos added`,
        status: "success",
        duration: 2500,
      });
      onSaved?.();
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err?.message || "Could not upload photo",
        status: "error",
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async (img) => {
    if (img.pending || isCreate) {
      setImages((prev) => {
        const next = prev.filter((item) => item.id !== img.id);
        if (img.url?.startsWith?.("blob:")) URL.revokeObjectURL(img.url);
        if (avatarUrl === img.url) setAvatarUrl(next[0]?.url || null);
        return next;
      });
      return;
    }

    if (!img.dbId) return;
    setUploadingPhoto(true);
    try {
      await RemoveImage(img.dbId);
      setImages((prev) => {
        const next = prev.filter((item) => item.id !== img.id);
        if (avatarUrl === img.url) setAvatarUrl(next[0]?.url || null);
        return next;
      });
      toast({ title: "Photo removed", status: "success", duration: 2500 });
      onSaved?.();
    } catch (err) {
      toast({
        title: "Remove failed",
        description: err?.message || "Could not remove photo",
        status: "error",
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const buildPreferences = () => {
    const ageNum = form.age ? parseInt(form.age, 10) : undefined;
    const prefs = {
      gender: form.gender || undefined,
      occupation: form.occupation.trim() || undefined,
      drinking: form.drinking || undefined,
      smoking: form.smoking || undefined,
      workout: form.workout || undefined,
      religion: form.religion || undefined,
      age: Number.isFinite(ageNum) ? ageNum : undefined,
    };
    Object.keys(prefs).forEach((k) => prefs[k] === undefined && delete prefs[k]);
    return prefs;
  };

  const handleSave = async () => {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();
    if (!firstName && !email) {
      toast({
        title: "Name or email required",
        description: "Add at least a first name or email.",
        status: "warning",
        duration: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      const ageNum = form.age ? parseInt(form.age, 10) : undefined;
      const preferences = buildPreferences();

      if (isCreate) {
        const id =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `admin-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        await CreateUser({
          id,
          firstName,
          lastName,
          email,
          bio: form.bio,
          country: form.country.trim() || "",
          education: form.education || "",
          isProfileComplete: form.isProfileComplete,
          isVerified: form.isVerified,
          isActive: true,
          preferences,
        });

        const pending = images.filter((img) => img.pending && img.file);
        for (const img of pending) {
          await uploadImageFile(id, img.file);
        }
        clearPendingImageUrls(pending);

        toast({
          title: "User created",
          description: pending.length
            ? `${pending.length} photo${pending.length === 1 ? "" : "s"} uploaded`
            : undefined,
          status: "success",
          duration: 3000,
        });
      } else {
        if (!userId) return;
        await UpdateUser({
          Id: userId,
          FirstName: firstName,
          LastName: lastName,
          Email: email,
          Bio: form.bio,
          Country: form.country.trim(),
          Education: form.education,
          Occupation: form.occupation,
          Gender: form.gender || undefined,
          Age: Number.isFinite(ageNum) ? ageNum : undefined,
          Drinking: form.drinking,
          Smoking: form.smoking,
          Workout: form.workout,
          Religion: form.religion,
          IsProfileComplete: form.isProfileComplete,
          IsVerified: form.isVerified,
        });
        toast({ title: "User updated", status: "success", duration: 3000 });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: isCreate ? "Create failed" : "Update failed",
        description: err?.response?.data?.message || err?.response?.data?.title || err.message,
        status: "error",
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleAccountActive = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      if (accountActive) {
        await DeactivateUser(userId);
        setAccountActive(false);
        toast({ title: "Account deactivated", status: "info" });
      } else {
        await ActivateUser(userId);
        setAccountActive(true);
        toast({ title: "Account activated", status: "success" });
      }
      onSaved?.();
    } catch (err) {
      toast({
        title: "Action failed",
        description: err?.response?.data?.message || err.message,
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const resolveGrantUntil = () => {
    if (grantDuration === "lifetime") return null;
    if (grantDuration === "1month") return addMonthsFromNow(1);
    if (grantDuration === "1year") return addMonthsFromNow(12);
    if (grantDuration === "custom" && customUntil) {
      return new Date(customUntil).toISOString();
    }
    return addMonthsFromNow(12);
  };

  const handleGrantPremium = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const premiumUntil = resolveGrantUntil();
      await grantManualPremium(userId, { premiumUntil });
      setPremiumInfo({
        isPremium: true,
        source: "manual",
        premiumUntil,
      });
      toast({ title: "Premium granted", status: "success", duration: 3000 });
      onSaved?.();
    } catch (err) {
      toast({
        title: "Grant failed",
        description: err?.message || "Could not grant premium",
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRevokePremium = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      await revokeManualPremium(userId);
      setPremiumInfo({ isPremium: false, source: "stripe", premiumUntil: null });
      toast({ title: "Premium revoked", status: "info", duration: 3000 });
      onSaved?.();
    } catch (err) {
      toast({
        title: "Revoke failed",
        description: err?.message || "Could not revoke premium",
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const displayName = [form.firstName, form.lastName].filter(Boolean).join(" ") || (isCreate ? "New user" : "User");

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={border}
        bg={panelBg}
        mx={3}
        maxH="90vh"
      >
        <ModalHeader
          borderBottomWidth="1px"
          borderColor={border}
          py={4}
          pr={12}
        >
          <Flex align="center" gap={3} minW={0}>
            <Box
              w="44px"
              h="44px"
              borderRadius="full"
              overflow="hidden"
              bg={photoTileBg}
              flexShrink={0}
              borderWidth="1px"
              borderColor={border}
            >
              {(avatarUrl || images[0]?.url) && !loading ? (
                <Box
                  as="img"
                  src={avatarUrl || images[0]?.url}
                  alt=""
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
            </Box>
            <Box minW={0}>
              <Text fontSize="lg" fontWeight="semibold" color={titleColor} letterSpacing="-0.02em" noOfLines={1}>
                {isCreate ? "Add user" : loading ? "Loading…" : displayName}
              </Text>
              <Text fontSize="xs" color={muted} mt={0.5} noOfLines={1} fontFamily={isCreate ? undefined : "mono"}>
                {isCreate ? "Creates a profile only — no login password" : userId}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton borderRadius="full" top={3} right={3} />

        <ModalBody py={5}>
          {loading ? (
            <Center py={12}>
              <Spinner color="brand.400" />
            </Center>
          ) : (
            <SimpleGrid columns={1} spacing={4}>
              <Box p={3} borderRadius="xl" borderWidth="1px" borderColor={border}>
                <Flex align="center" justify="space-between" mb={2} gap={2}>
                  <SectionTitle>Photos</SectionTitle>
                  <Flex align="center" gap={2}>
                    <Text fontSize="xs" color={muted} fontWeight="medium">
                      {images.length} photo{images.length === 1 ? "" : "s"}
                    </Text>
                    <Button
                      size="xs"
                      borderRadius="full"
                      leftIcon={<Plus size={14} />}
                      variant="outline"
                      onClick={handlePickPhotos}
                      isLoading={uploadingPhoto}
                      isDisabled={saving}
                    >
                      Add
                    </Button>
                  </Flex>
                </Flex>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handlePhotoFilesSelected}
                />
                {images.length === 0 ? (
                  <Box
                    as="button"
                    type="button"
                    w="full"
                    py={6}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderStyle="dashed"
                    borderColor={border}
                    bg={photoTileBg}
                    onClick={handlePickPhotos}
                    disabled={uploadingPhoto || saving}
                    _hover={{ borderColor: "brand.400" }}
                  >
                    <Text fontSize="sm" color={muted}>
                      {isCreate
                        ? "Add photos now — they’ll upload when you create the user"
                        : "No photos yet — click to add"}
                    </Text>
                  </Box>
                ) : (
                  <Box overflowX="auto" sx={{ WebkitOverflowScrolling: "touch" }}>
                    <Flex gap={2} minW="min-content" pb={1} align="stretch">
                      {images.map((img, index) => (
                        <Box
                          key={img.id || img.url || index}
                          position="relative"
                          w="96px"
                          h="128px"
                          flexShrink={0}
                          borderRadius="lg"
                          overflow="hidden"
                          bg={photoTileBg}
                          borderWidth="1px"
                          borderColor={border}
                        >
                          <Box
                            as="img"
                            src={img.url}
                            alt={`Photo ${index + 1}`}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                            onError={(e) => {
                              e.currentTarget.style.opacity = "0.2";
                            }}
                          />
                          {index === 0 ? (
                            <Text
                              position="absolute"
                              bottom={1}
                              left={1}
                              px={1.5}
                              py={0.5}
                              borderRadius="md"
                              fontSize="9px"
                              fontWeight="bold"
                              bg="blackAlpha.700"
                              color="white"
                            >
                              Primary
                            </Text>
                          ) : null}
                          <IconButton
                            aria-label="Remove photo"
                            icon={<Trash2 size={14} />}
                            size="xs"
                            position="absolute"
                            top={1}
                            right={1}
                            borderRadius="full"
                            colorScheme="red"
                            bg="blackAlpha.700"
                            _hover={{ bg: "red.500" }}
                            onClick={() => handleRemovePhoto(img)}
                            isDisabled={uploadingPhoto || saving}
                          />
                        </Box>
                      ))}
                      <Box
                        as="button"
                        type="button"
                        w="96px"
                        h="128px"
                        flexShrink={0}
                        borderRadius="lg"
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderColor={border}
                        bg={photoTileBg}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        onClick={handlePickPhotos}
                        disabled={uploadingPhoto || saving}
                        _hover={{ borderColor: "brand.400" }}
                      >
                        <Plus size={22} opacity={0.55} />
                      </Box>
                    </Flex>
                  </Box>
                )}
              </Box>

              <Box p={3} borderRadius="xl" bg={sectionTint} borderWidth="1px" borderColor={border}>
                <SectionTitle>Profile</SectionTitle>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={2}>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      First name
                    </FormLabel>
                    <Input
                      value={form.firstName}
                      onChange={(e) => setField("firstName", e.target.value)}
                      {...inputProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Last name
                    </FormLabel>
                    <Input
                      value={form.lastName}
                      onChange={(e) => setField("lastName", e.target.value)}
                      {...inputProps}
                    />
                  </FormControl>
                </SimpleGrid>
                <FormControl mt={3}>
                  <FormLabel fontSize="xs" color={muted}>
                    Email
                  </FormLabel>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    {...inputProps}
                  />
                </FormControl>
                <FormControl mt={3}>
                  <FormLabel fontSize="xs" color={muted}>
                    Bio
                  </FormLabel>
                  <Textarea
                    value={form.bio}
                    onChange={(e) => setField("bio", e.target.value)}
                    minH="72px"
                    {...inputProps}
                  />
                </FormControl>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={3}>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Age
                    </FormLabel>
                    <Input
                      type="number"
                      value={form.age}
                      onChange={(e) => setField("age", e.target.value)}
                      {...inputProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Gender
                    </FormLabel>
                    <Select
                      value={form.gender}
                      onChange={(e) => setField("gender", e.target.value)}
                      placeholder="—"
                      {...inputProps}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box p={3} borderRadius="xl" borderWidth="1px" borderColor={border}>
                <SectionTitle>Details</SectionTitle>
                <SimpleGrid columns={1} spacing={3} mt={2}>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Country
                    </FormLabel>
                    <Input
                      value={form.country}
                      onChange={(e) => setField("country", e.target.value)}
                      {...inputProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Occupation
                    </FormLabel>
                    <Input
                      value={form.occupation}
                      onChange={(e) => setField("occupation", e.target.value)}
                      {...inputProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Education
                    </FormLabel>
                    <Select
                      value={form.education}
                      onChange={(e) => setField("education", e.target.value)}
                      placeholder="—"
                      {...inputProps}
                    >
                      {EDUCATIONS.map((e) => (
                        <option key={e} value={e}>
                          {e}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box p={3} borderRadius="xl" borderWidth="1px" borderColor={border}>
                <SectionTitle>Lifestyle</SectionTitle>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={2}>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Drinking
                    </FormLabel>
                    <Select
                      value={form.drinking}
                      onChange={(e) => setField("drinking", e.target.value)}
                      placeholder="Select…"
                      {...inputProps}
                    >
                      {optionsWithCurrent(DRINKING_OPTIONS, form.drinking).map((v) => (
                        <option key={v} value={v}>
                          {v === "Occasinally" ? "Occasionally" : v}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Smoking
                    </FormLabel>
                    <Select
                      value={form.smoking}
                      onChange={(e) => setField("smoking", e.target.value)}
                      placeholder="Select…"
                      {...inputProps}
                    >
                      {optionsWithCurrent(SMOKING_OPTIONS, form.smoking).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Workout
                    </FormLabel>
                    <Select
                      value={form.workout}
                      onChange={(e) => setField("workout", e.target.value)}
                      placeholder="Select…"
                      {...inputProps}
                    >
                      {optionsWithCurrent(WORKOUT_OPTIONS, form.workout).map((v) => (
                        <option key={v} value={v}>
                          {v === "occasionally"
                            ? "Occasionally"
                            : v === "Active few times week"
                              ? "Active (a few times a week)"
                              : v === "Very active"
                                ? "Very active (almost daily)"
                                : v === "Fitness lifestyle"
                                  ? "Fitness is my lifestyle"
                                  : v}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color={muted}>
                      Religion
                    </FormLabel>
                    <Select
                      value={form.religion}
                      onChange={(e) => setField("religion", e.target.value)}
                      placeholder="Select…"
                      {...inputProps}
                    >
                      {optionsWithCurrent(RELIGION_OPTIONS, form.religion).map((v) => (
                        <option key={v} value={v}>
                          {v === "Christian  Catholic"
                            ? "Christian – Catholic"
                            : v === "Christian  Protestant"
                              ? "Christian – Protestant"
                              : v === "Christian  Other"
                                ? "Christian – Other"
                                : v}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box p={3} borderRadius="xl" borderWidth="1px" borderColor={border}>
                <SectionTitle>Flags</SectionTitle>
                <SimpleGrid columns={1} spacing={3} mt={2}>
                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel fontSize="sm" mb={0} color={titleColor}>
                      Profile complete
                    </FormLabel>
                    <Switch
                      isChecked={form.isProfileComplete}
                      onChange={(e) => setField("isProfileComplete", e.target.checked)}
                      colorScheme="green"
                    />
                  </FormControl>
                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel fontSize="sm" mb={0} color={titleColor}>
                      Verified
                    </FormLabel>
                    <Switch
                      isChecked={form.isVerified}
                      onChange={(e) => setField("isVerified", e.target.checked)}
                      colorScheme="green"
                    />
                  </FormControl>
                  {!isCreate ? (
                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel fontSize="sm" mb={0} color={titleColor}>
                      Account active
                    </FormLabel>
                    <Switch
                      isChecked={accountActive !== false}
                      onChange={toggleAccountActive}
                      colorScheme="green"
                      isDisabled={saving}
                    />
                  </FormControl>
                  ) : null}
                </SimpleGrid>
              </Box>

              {!isCreate ? (
              <Box p={3} borderRadius="xl" borderWidth="1px" borderColor={border} bg={sectionTint}>
                <SectionTitle>Premium access</SectionTitle>
                <Text fontSize="xs" color={muted} mt={1} mb={3}>
                  {premiumInfo.isPremium
                    ? premiumInfo.source === "manual"
                      ? `Manual grant${premiumInfo.premiumUntil ? ` · until ${premiumUntilLabel(premiumInfo)}` : " · lifetime"}`
                      : "Active via Stripe subscription"
                    : "No premium access"}
                </Text>
                <FormControl mb={3}>
                  <FormLabel fontSize="xs" color={muted}>
                    Grant duration
                  </FormLabel>
                  <Select
                    value={grantDuration}
                    onChange={(e) => setGrantDuration(e.target.value)}
                    {...inputProps}
                  >
                    <option value="1month">1 month</option>
                    <option value="1year">1 year</option>
                    <option value="lifetime">Lifetime</option>
                    <option value="custom">Custom date</option>
                  </Select>
                </FormControl>
                {grantDuration === "custom" && (
                  <FormControl mb={3}>
                    <FormLabel fontSize="xs" color={muted}>
                      Expires on
                    </FormLabel>
                    <Input
                      type="date"
                      value={customUntil}
                      onChange={(e) => setCustomUntil(e.target.value)}
                      {...inputProps}
                    />
                  </FormControl>
                )}
                <Flex gap={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    borderRadius="full"
                    bgGradient="linear(to-r, brand.300, brand.500)"
                    color="gray.900"
                    onClick={handleGrantPremium}
                    isLoading={saving}
                    isDisabled={loading}
                  >
                    Grant premium
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    borderRadius="full"
                    onClick={handleRevokePremium}
                    isLoading={saving}
                    isDisabled={loading || !premiumInfo.isPremium}
                  >
                    Revoke premium
                  </Button>
                </Flex>
              </Box>
              ) : null}
            </SimpleGrid>
          )}
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={border} gap={2}>
          <Button variant="ghost" borderRadius="full" onClick={onClose}>
            Cancel
          </Button>
          <Button
            borderRadius="full"
            bgGradient="linear(to-r, brand.300, brand.500)"
            color="gray.900"
            fontWeight="semibold"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={loading || uploadingPhoto}
            px={6}
          >
            {isCreate ? "Create user" : "Save changes"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
